import React, { memo, useCallback, useState, useEffect } from "react";
import { DatePicker } from 'tea-component';
import YearRangePicker from '@gp/components/TeaRangePicker';
import { noop } from '@gp/utils/common';
import { getDefaultSelectDate, getQuarter } from './utils/common';
import calendarSvg from '@gp/assets/svg/calendar.svg';
import moment from 'moment';
import './index.scss';

const { RangePicker } = DatePicker;

interface DateSelectorPropsType {
  loading: boolean,
  range: string,
  dateType: dateTypes,
  monthOnFocus: (e: any) => void,
  yearOnFocus: (e: any) => void,
  onChange: (v: string[], type: dateTypes) => void,
}

const DEFAULT_PROPS: DateSelectorPropsType = {
  loading: true,
  range: '',
  dateType: 'quarter',
  monthOnFocus: noop,
  yearOnFocus: noop,
  onChange: noop,
};

function DateSelector(props: DateSelectorPropsType) {
  const {
    loading,
    range,
    dateType,
    monthOnFocus,
    yearOnFocus,
    onChange,
  } = props;

  const [monthRange, setMonthRange] = useState<string>(''); // 2022.01-2023.02
  const [quarterRange, setQuarterRange] = useState<string>(''); // 2022.Q1-2023.Q2
  const [yearRange, setYearRange] = useState<string>(''); // 2022-2023
  const [monthSelect, setMonthSelect] = useState<string>(''); // 2022.01-2023.02
  const [quarterSelect, setQuarterSelect] = useState<string>(''); // 2022.Q1-2023.Q2
  const [yearSelect, setYearSelect] = useState<string>(''); // 2022-2023

  const init = useCallback((dateType: dateTypes, range: string) => {
    if (!range) return;

    switch (dateType) {
      // 月
      case 'month':
        {
          const startRange = `${range?.slice(0, 4)}.${range?.slice(4, 6)}`;
          const endRange = `${range?.slice(9, 13)}.${range?.slice(13, 15)}`;
          setMonthRange(startRange + '-' + endRange);
          const {
            defaultStartV,
            defaultEndV,
          } = getDefaultSelectDate(
            startRange,
            endRange,
            'YYYY.MM',
            'month',
          );
          if (defaultStartV && defaultEndV) {
            const defaultSelectMonth = moment(defaultStartV).format('YYYY.MM') + '-' + moment(defaultEndV).format('YYYY.MM');
            setMonthSelect(defaultSelectMonth);
          }
        }
        break;

      // 季度
      case 'quarter':
        {
          const startRange = `${range?.slice(0, 4)}.Q${getQuarter(range?.slice(4, 6))}`;
          const endRange = `${range?.slice(9, 13)}.Q${getQuarter(range?.slice(13, 15))}`;
          setQuarterRange(startRange + '-' + endRange);
          const {
            defaultStartV,
            defaultEndV,
          } = getDefaultSelectDate(
            startRange,
            endRange,
            'YYYY.Q',
            'quarter',
          );
          if (defaultStartV && defaultEndV) {
            const tempDefaultSelectMonth = moment(defaultStartV).format('YYYY.Q') + '-' + moment(defaultEndV).format('YYYY.Q');
            const defaultSelectMonth = tempDefaultSelectMonth?.slice(0, 5) + 'Q' + tempDefaultSelectMonth?.slice(5, 12) + 'Q' + tempDefaultSelectMonth?.slice(12);
            setQuarterSelect(defaultSelectMonth);
          }
        }
        break;

      // 年
      case 'year':
        {
          const startRange = range?.slice(0, 4);
          const endRange = range?.slice(9, 13);
          setYearRange(startRange + '-' + endRange);
          const {
            defaultStartV,
            defaultEndV,
          } = getDefaultSelectDate(
            startRange,
            endRange,
            'YYYY',
            'year',
          );
          if (defaultStartV && defaultEndV) {
            const defaultSelectMonth = moment(defaultStartV).format('YYYY') + '-' + moment(defaultEndV).format('YYYY');
            setYearSelect(defaultSelectMonth);
          }
        }
        break;

      default:
        break;
    }
  }, []);

  const monthHandleChange = useCallback((value: moment.Moment[]) => {
    // 点击清空
    /* if (!value || !value[0] || !value[1]) {
      setMonthSelect('');
      return;
    } */
    const newMonthRange = value[0]?.format('YYYY.MM') + '-' + value[1]?.format('YYYY.MM');
    setMonthSelect(newMonthRange);
  }, []);

  const quarterHandleChange = useCallback((value: moment.Moment[]) => {
    // 点击清空
    /* if (!value || !value[0] || !value[1]) {
      setQuarterSelect('');
      return;
    } */
    let newQuarterRange = value[0]?.format('YYYY.Q') + '-' + value[1]?.format('YYYY.Q');
    newQuarterRange = newQuarterRange?.slice(0, 5) + 'Q' + newQuarterRange?.slice(5, 12) + 'Q' + newQuarterRange?.slice(12);
    setQuarterSelect(newQuarterRange);
  }, []);

  const yearHandleChange = useCallback((value: moment.Moment[], type) => {
    // 点击清空
    /* if (!value || !value[0] || !value[1]) {
      setYearSelect('');
      return;
    } */
    const newYearRange = value[0]?.format('YYYY') + '-' + value[1]?.format('YYYY');
    setYearSelect(newYearRange);
  }, []);

  const getDisabledConfig = useCallback((date: moment.Moment) => {
    // 筛选器对应的时间开始和结束时间毫秒
    const startTime = moment(date)?.valueOf(),
          endTime = moment(date)?.valueOf();
    // 允许可选的时间的开始和结束时间毫秒
    const allowStartTime = moment(range?.slice(0, 8), 'YYYYMMDD')?.startOf(dateType)?.valueOf(),
          allowEndTime = moment(range?.slice(9), 'YYYYMMDD')?.endOf(dateType)?.valueOf();
    return (startTime >= allowStartTime) && (endTime <= allowEndTime);
  }, [range, dateType]);

  // 初始化与筛选器粒度切换
  useEffect(() => {
    if (loading) return;
    init(dateType, range);

    // 去除月筛选器聚焦的输入功能
    if (dateType === 'month') {
      const e = document.querySelectorAll('.calendar-filter-date-month-selector .tea-input');
      for (let i = 0; i < e?.length; i ++) {
        const t = e?.[i];
        if (t) {
          t.addEventListener('focus', monthOnFocus);
        }
      }
    }
    // 去除年筛选器聚焦的输入功能
    if (dateType === 'year') {
      const e = document.querySelectorAll('.calendar-filter-date-year-selector .tea-input');
      for (let i = 0; i < e?.length; i ++) {
        const t = e?.[i];
        if (t) {
          t.addEventListener('focus', yearOnFocus);
        }
      }
    }
  }, [dateType, range, loading]);

  // 触发onChange规则
  useEffect(() => {
    switch (true) {
      case dateType === 'month' && !!monthSelect:
        onChange([monthSelect?.split('-')?.[0] || '', monthSelect?.split('-')?.[1] || ''], dateType);
        return;
      case dateType === 'quarter' && !!quarterSelect:
        onChange([quarterSelect?.split('-')?.[0] || '', quarterSelect?.split('-')?.[1] || ''], dateType);
        return;
      case dateType === 'year' && !!yearSelect:
        onChange([yearSelect?.split('-')?.[0] || '', yearSelect?.split('-')?.[1] || ''], dateType);
        return;
      default:
        break;
    }
  }, [monthSelect, quarterSelect, yearSelect]);
  useEffect(() => {
    switch (true) {
      case dateType === 'month' && !!monthSelect:
        onChange([monthSelect?.split('-')?.[0] || '', monthSelect?.split('-')?.[1] || ''], dateType);
        return;
      case dateType === 'quarter' && !!quarterSelect:
        onChange([quarterSelect?.split('-')?.[0] || '', quarterSelect?.split('-')?.[1] || ''], dateType);
        return;
      case dateType === 'year' && !!yearSelect:
        onChange([yearSelect?.split('-')?.[0] || '', yearSelect?.split('-')?.[1] || ''], dateType);
        return;
      default:
        break;
    }
  }, [dateType]);

  return (
    <section className="calendar-filter-date-picker-selector font14">
      <>
        {/* 禁用与Loading */}
        {(loading || !range) && (
          <section className="calendar-filter-date-picker-loading">
            <span className="loading-text loading-text-one">{loading ? 'Loading...' : 'Select Date'}</span>
            <span className="loading-text loading-text-two">{loading ? 'Loading...' : 'Select Date'}</span>
            {/* <img className="loading-calendar-icon" width={16} height={16} src={calendarSvg} alt="" /> */}
          </section>
        )}
        {dateType === 'month' && (
          <RangePicker
            className="calendar-filter-date-month-selector"
            style={{width: 220}}
            // overlayClassName="gp-model-font14"
            // clearable
            separator="~"
            calendarType="month"
            value={(loading || !monthSelect) ? undefined : [
              moment(monthSelect?.split('-')?.[0], 'YYYY.MM')?.startOf("month"),
              moment(monthSelect?.split('-')?.[1], 'YYYY.MM')?.endOf("month"),
            ]}
            disabledQuarter={getDisabledConfig}
            range={[
              moment(monthRange?.split('-')?.[0], 'YYYY.MM')?.startOf("month"),
              moment(monthRange?.split('-')?.[1], 'YYYY.MM')?.endOf("month"),
            ]}
            onChange={monthHandleChange}
          />
        )}
        {dateType === 'quarter' && (
          <RangePicker
            className="calendar-filter-date-quarter-selector"
            // style={{width: 196}}
            // overlayClassName="gp-model-font14"
            // clearable
            separator="~"
            calendarType="quarter"
            value={(loading || !quarterSelect) ? undefined : [
              moment(quarterSelect?.split('-')?.[0], 'YYYY.Q')?.startOf("quarter"),
              moment(quarterSelect?.split('-')?.[1], 'YYYY.Q')?.endOf("quarter"),
            ]}
            disabledQuarter={getDisabledConfig}
            range={[
              moment(quarterRange?.split('-')?.[0], 'YYYY.Q')?.startOf("quarter"),
              moment(quarterRange?.split('-')?.[1], 'YYYY.Q')?.endOf("quarter"),
            ]}
            onChange={quarterHandleChange}
          />
        )}
        {dateType === 'year' && (
          <YearRangePicker
            className="calendar-filter-date-year-selector"
            showShortcut={false}
            showLabelPrefix={false}
            showSelectType={false}
            type="year"
            startOfWeek={0}
            language="en"
            value={(loading || !yearSelect) ? undefined : [
              moment(yearSelect?.split('-')?.[0], 'YYYY')?.startOf("year"),
              moment(yearSelect?.split('-')?.[1], 'YYYY')?.endOf("year"),
            ]}
            typeList={['year']}
            range={[
              moment(yearRange?.split('-')?.[0], 'YYYY')?.startOf("year"),
              moment(yearRange?.split('-')?.[1], 'YYYY')?.endOf("year"),
            ]}
            onChange={yearHandleChange}
          />
        )}
      </>
    </section>
  );
}

DateSelector.defaultProps = DEFAULT_PROPS;

export default memo(DateSelector);
